import { attributionForImageUrl, wikimediaFilePageUrl } from './imageSource';

/**
 * The photo set a swipeable place hero shows — the planner map card
 * (usePlacePhotos) and the place page's hero build it here, so the two agree
 * on order and on what counts as the same photo.
 *
 * Order (Kurtis, 2026-10-31):
 *
 *   1. the cover the caller already rendered (the cascade's pick —
 *      server/services/photoSourceRegistry.js), never moved
 *   2. licensed photos: Tiqets' images of the place, then its Commons cover +
 *      gallery (server/services/placePhotoSetService.js) — professional
 *      photos lead
 *   3. community photos (admin-approved public uploads, plus the viewer's own)
 *
 * Every photo appears once. The cover is very often one of the rows behind it
 * (the cascade's Tiqets tier picks Tiqets' first image, its Commons tier the
 * landmark's cover, its community tiers the featured upload), and the same
 * image reaches the client under different URLs — a ?v= cache-buster, a size
 * segment, an imgix w/h, a Commons thumb width — so rows are compared by the
 * image they name (photoIdentityKey), not by URL.
 *
 * Every emitted photo carries an `attribution` ({ text, uri } or null). For
 * Wikimedia and Tiqets photos the credit is a licence/terms condition, so it
 * travels WITH the photo: rich attribution (author + licence, the Tiqets
 * photographer) from the row, the host-level credit as the fallback.
 */

// Normalize the two attribution shapes in circulation — a bare string, or the
// { text, uri } object attributionForImageUrl returns when it can derive a
// Commons file page — into one object shape, so consumers render one thing.
// CC BY-SA §3(a)(2) treats the hyperlink as what satisfies attribution, so the
// uri is kept whenever there is one.
export function normalizeAttribution(value) {
  if (!value) return null;
  if (typeof value === 'string') return { text: value, uri: null };
  const text = value.text || null;
  return text ? { text, uri: value.uri || null } : null;
}

const TIQETS_HOST = 'aws-tiqets-cdn.imgix.net';

/**
 * What image a URL names, independent of size and cache-buster, or null when
 * it can't be told (an unknown host — compared by full URL instead).
 *
 *   user photo   the token, in /api/user-photos/<token> or <cdn>/photos/<token>/<size>
 *   Tiqets       the imgix path (the query carries only the render size)
 *   Commons      the file name (thumb and original URLs of one file share it)
 */
export function photoIdentityKey(url) {
  if (typeof url !== 'string' || !url) return null;
  const user = url.match(/\/api\/user-photos\/([^/?#]+)/)
    || url.match(/\/photos\/([A-Za-z0-9_-]{16})\/(?:thumb|medium|large|xlarge|full)(?:[?#]|$)/);
  if (user) return `user:${user[1]}`;
  if (url.includes(TIQETS_HOST)) return `tiqets:${url.split(/[?#]/)[0]}`;
  const commons = wikimediaFilePageUrl(url);
  if (commons) return `commons:${commons}`;
  return null;
}

const sized = (photo) => (photo.width > 0 && photo.height > 0
  ? { width: photo.width, height: photo.height }
  : null);

/**
 * @param {object} opts
 * @param {string|null} [opts.cover]             the URL the caller rendered first
 * @param {string} [opts.coverAlt]
 * @param {string|object|null} [opts.coverAttribution]  rich credit for the cover
 * @param {number|null} [opts.coverWidth]         its pixel size, when known
 * @param {number|null} [opts.coverHeight]
 * @param {object[]} [opts.licensed]   placePhotoSetService rows
 *   ({ id, url, source, attribution_text, view_label? })
 * @param {object[]} [opts.community]  community-photo rows (API shape:
 *   photo_data_base64, width, height, description, user_name, user_id)
 * @param {function} [opts.captionFor] (licensedRow) => caption string
 * @returns {object[]} [{ id, url, alt, attribution, width?, height?, caption?,
 *   sourceLabel?, userId? }]
 */
export function buildPlacePhotoSet({
  cover = null,
  coverAlt = '',
  coverAttribution = null,
  coverWidth = null,
  coverHeight = null,
  licensed = [],
  community = [],
  captionFor = null
} = {}) {
  const out = [];
  const seen = new Set();
  // False when the row is a photo already in the set.
  const claim = (url) => {
    const key = photoIdentityKey(url) || `url:${url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };

  if (cover) {
    claim(cover);
    out.push({
      id: 'cover',
      url: cover,
      alt: coverAlt,
      // Omitted rather than sent as null when unknown, so a consumer's
      // "does this row declare a size" check stays a simple truthiness test.
      ...sized({ width: coverWidth, height: coverHeight }),
      // Rich attribution wins: the author + licence text the server resolved
      // is not derivable from the URL, and the host-level fallback would
      // replace a named photographer with a bare "Photo: Wikimedia Commons".
      attribution: normalizeAttribution(coverAttribution) || normalizeAttribution(attributionForImageUrl(cover))
    });
  }

  for (const photo of licensed || []) {
    if (!photo?.url || !claim(photo.url)) continue;
    out.push({
      id: photo.id,
      url: photo.url,
      alt: photo.alt || coverAlt,
      caption: captionFor ? captionFor(photo) : '',
      // The row's own credit — never the cover's — so a credit can't bleed
      // onto a photo that is not the one it names.
      attribution: normalizeAttribution(
        photo.attribution_text
          ? { text: photo.attribution_text, uri: wikimediaFilePageUrl(photo.url) }
          : attributionForImageUrl(photo.url)
      )
    });
  }

  for (const photo of community || []) {
    const url = photo?.photo_data_base64;
    if (!url || !claim(url)) continue;
    out.push({
      id: `community_${photo.id}`,
      url,
      alt: coverAlt,
      // The server selects these explicitly (migration 234) so a surface can
      // size a photo frame before the bytes arrive. Same omit-when-unknown
      // shape as the cover row.
      ...sized(photo),
      caption: photo.description || '',
      sourceLabel: photo.user_name || '',
      userId: photo.user_id,
      // Community rows are user uploads, so this is null in practice.
      attribution: normalizeAttribution(attributionForImageUrl(photo.photo_data_base64))
    });
  }

  return out;
}
